import useTitanApp from "@/hooks/useTitanApp";
import { useState, useEffect } from "react";
import {
  setConsultationId,
  setIsConsultationEnabled,
  setIsFinalSummary,
  setPatientNotes,
  setPatientTabSwitcher,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setSignatureDetails,
  setTranscript,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  getPreConsultationByConsultationId,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  getPaymentAuditByConsultation,
  setHasPreConsultTab,
  setPatientAIChatMessages,
  setDemDxSummary,
  setIsLocallyCompleted,
  setConsultationAuditTimeline,
  setVitalsDetails,
  setIncrementSignatureCompleted,
  setPaymentAudit,
  setPreConsult,
  setIsSummaryStreaming,
  setisManualSubmited,
  setConsultationPaused,
  setSnomedCodes,
  setAccessRecord,
} from "@/dispatchers/patientDispatchers";
import {
  setSelectedTemplate,
  setIsTemplateSelected,
  setStreamedText,
} from "@/dispatchers/templateDispatchers";
import { useNavigate } from "react-router";
import { setPatientInfoTab } from "@/dispatchers/appDispatchers";
import moment from "moment";
import { useNHS111Data } from "../nhs111/nhs111Data";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

export interface PatientInfo {
  name: string;
  address: string;
  dateOfBirth: string;
  sex: string;
  nhsNumber: string;
  fullAddress: string;
  ethnicity: string;
  surgery: string;
  email: string;
  phone: string;
  homeNumber: string;
  contacts?: { type: string; value: string }[];
  ods?:string;
}

export interface ActiveConsultation {
  id: string;
  startTime: string;
  startDate: string;
  type: string;
  status: string;
  duration: string;
  lastActivity: string;
}

export const defaultPatientData: PatientInfo = {
  name: "Jessica Aubree",
  address: "154 Albany St, London",
  dateOfBirth: "18/10/1968 (51)",
  sex: "Female",
  nhsNumber: "630 783 7632",
  fullAddress: "154 Albany St, London, NW1 4DH, UK",
  ethnicity: "White - Irish",
  surgery: "TITAN Pharmacy",
  email: "jess.aubree@gmail.com",
  phone: "+44 (0) 7776 495586",
  homeNumber: "+44 (0) 7776 495586",
};

export const defaultActiveConsultation: ActiveConsultation = {
  id: "CONS-2024-001",
  startTime: "14:30",
  startDate: "Today",
  type: "Medication Review",
  status: "In Progress",
  duration: "25 min",
  lastActivity: "2 min ago",
};

export const availablePharmacists = [
  "Dr. Sarah Johnson",
  "Dr. Michael Chen",
  "Dr. Emma Wilson",
  "Dr. James Smith",
];

export const usePatientFormState = () => {
  const { patientState, appState } = useTitanApp();
  const { isPatientDetailsLoading, preConsultData } = patientState || {};

  const { currentPharmacyId, darkMode } = appState || {};
  const navigate = useNavigate();
  const { setNHS111ConsultationSummary } = useNHS111Data();

  const getActionDetails = (action: any) => {
    const actionName = action.action?.actionName || action.actionName;

    let type:
      | "prescription"
      | "payment"
      | "vaccine"
      | "referral"
      | "vitals"
      | "appointment"
      | "system"
      | "signature" = "system";

    let details: any[] = [];
    let vitalsData: any = null;
    let signatureData: any = null;
    let vaccineData: any = null;

    if (actionName === "PrescribeMedication") {
      type = "prescription";
      try {
        const payload = action.action?.payload || action.payload;
        if (payload) {
          const parsed =
            typeof payload === "string" ? JSON.parse(payload) : payload;
          if (parsed.prescriptionsDetails) {
            details = parsed.prescriptionsDetails.map((p: any) => ({
              value: p.value,
              dose: p.dose,
              quantity: p.quantity,
            }));
          }
        }
      } catch (err) {
        console.warn("Invalid payload JSON for prescription");
      }
    } else if (actionName === "Payment") {
      type = "payment";
    } else if (actionName === "Vaccinations") {
      type = "vaccine";
      try {
        const payload = action.action?.payload || action.payload;
        if (payload) {
          const parsed =
            typeof payload === "string" ? JSON.parse(payload) : payload;
          vaccineData = parsed;

          if (
            parsed.vaccinationDetails?.vaccinations &&
            Array.isArray(parsed.vaccinationDetails.vaccinations)
          ) {
            const vaccines = parsed.vaccinationDetails.vaccinations;
            details = vaccines.map((v: any) => ({
              vaccineName: v.vaccineName || "Unknown Vaccine",
              dose: v.dose || "",
              schedule: v.schedule || "",
              site: v.site || "",
              manufacturer: v.manufacturer || "",
              batchNumber: v.batchNumber || "",
              expiryDate: v.expiryDate || "",
              nextDueDate: v.nextDueDate || "",
              note: v.note || "",
            }));
          }
        }
      } catch (err) {
        console.warn("Invalid payload JSON for vaccine");
        details = [{ label: "Type", value: "Vaccination Record" }];
      }
    } else if (actionName === "SendReferralLetter") {
      type = "referral";
      details = [{ label: "Referral", value: "Referred to GP" }];
    } else if (actionName === "RecordVitals") {
      type = "vitals";
      try {
        const payload = action.action?.payload || action.payload;
        if (payload) {
          const parsed =
            typeof payload === "string" ? JSON.parse(payload) : payload;
          if (parsed.vitalsDetails) {
            vitalsData = parsed.vitalsDetails;
            const vitalsDetails = parsed.vitalsDetails;

            details = [
              {
                name: "recordedAt",
                value: vitalsDetails.recordedAt || "",
                unit: "",
              },
              {
                name: "vitalType",
                value: vitalsDetails.vitalType || "",
                unit: "",
              },
              {
                name: "vitalTypeKey",
                value: vitalsDetails.vitalTypeKey || "",
                unit: "",
              },
              {
                name: "serviceName",
                value: vitalsDetails.serviceName || "",
                unit: "",
              },
            ];

            const vitalTypeKey = vitalsDetails.vitalTypeKey;

            if (
              vitalTypeKey === "bloodpressure" &&
              vitalsDetails.bloodPressure
            ) {
              const bp = vitalsDetails.bloodPressure;
              details.push(
                {
                  name: "bpSystolic",
                  value: bp.Systolic?.Value || "",
                  unit: bp.Systolic?.Unit || "mmHg",
                },
                {
                  name: "bpDiastolic",
                  value: bp.Diastolic?.Value || "",
                  unit: bp.Diastolic?.Unit || "mmHg",
                },
              );
            } else if (
              vitalTypeKey === "circumference" &&
              vitalsDetails.circumferenceValue
            ) {
              const circ = vitalsDetails.circumferenceValue;
              const unit = circ.Unit || "cm";
              if (circ.Waist)
                details.push({ name: "waist", value: circ.Waist, unit });
              if (circ.Hip)
                details.push({ name: "hip", value: circ.Hip, unit });
              if (circ.Neck)
                details.push({ name: "neck", value: circ.Neck, unit });
            } else if (vitalTypeKey === "bmicalculator") {
              if (vitalsDetails.bmi) {
                details.push({
                  name: "bmi",
                  value: vitalsDetails.bmi.Value || "",
                  unit: vitalsDetails.bmi.Unit || "",
                });
              }
              if (vitalsDetails.height) {
                details.push({
                  name: "height",
                  value: vitalsDetails.height.Value || "",
                  unit: vitalsDetails.height.Unit || "",
                });
              }
              if (vitalsDetails.weight) {
                details.push({
                  name: "weight",
                  value: vitalsDetails.weight.Value || "",
                  unit: vitalsDetails.weight.Unit || "",
                });
              }
            } else {
              const vitalKey = Object.keys(vitalsDetails).find(
                (key) =>
                  key === vitalTypeKey ||
                  key === vitalTypeKey.toLowerCase() ||
                  (typeof vitalsDetails[key] === "object" &&
                    vitalsDetails[key]?.Value !== undefined),
              );

              if (vitalKey && vitalsDetails[vitalKey]) {
                const vitalData = vitalsDetails[vitalKey];
                if (vitalData.Value !== undefined) {
                  details.push({
                    name: vitalTypeKey,
                    value: vitalData.Value,
                    unit: vitalData.Unit || "",
                  });
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn("Invalid payload JSON for vitals", err);
      }
    } else if (actionName === "BookAppointment") {
      type = "appointment";
      try {
        const payload = action.action?.payload || action.payload;
        if (payload) {
          const parsed =
            typeof payload === "string" ? JSON.parse(payload) : payload;
          details = [
            {
              label: "Start Date",
              value: moment(parsed.startDate).format("DD/MM/YYYY HH:mm"),
            },
            {
              label: "End Date",
              value: moment(parsed.endDate).format("DD/MM/YYYY HH:mm"),
            },
            {
              label: "Duration",
              value: `${parsed.duration} ${parsed.durationUnit}`,
            },
          ];
        }
      } catch (err) {
        console.warn("Invalid payload JSON for appointment");
      }
    } else if (actionName === "Signature") {
      type = "signature";
      try {
        const payload = action.action?.payload || action.payload;
        if (payload) {
          const parsed =
            typeof payload === "string" ? JSON.parse(payload) : payload;
          signatureData = parsed;

          details = [
            { label: "Type", value: "Patient Signature" },
            {
              label: "Consent",
              value: parsed.consentStatement || "Consent provided",
            },
            {
              label: "Patient",
              value: parsed.patientRelationStatus?.name || "N/A",
            },
            {
              label: "Relationship",
              value: parsed.patientRelationStatus?.relationship || "Self",
            },
            {
              label: "Signed At",
              value: parsed.signedAt
                ? moment(parsed.signedAt).format("DD/MM/YYYY HH:mm")
                : "N/A",
            },
          ];
        }
      } catch (err) {
        console.warn("Invalid payload JSON for signature");
        details = [{ label: "Type", value: "Patient Signature" }];
      }
    } else if (actionName === "LibraryDocument") {
      type = "system";
      details = [{ label: "Type", value: "Library Document" }];
    } else if (actionName === "Document") {
      type = "system";
      details = [{ label: "Type", value: "Patient Document" }];
    }

    return { type, details, vitalsData, signatureData, vaccineData };
  };

  const handleArrowClick = async (patientId, consultationData) => {
    setPatientAIChatMessages([]);
    setDemDxSummary(null);

    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(null);
    setTranscript("");
    setIsLocallyCompleted(false);
    setConsultationPaused(false);
    setConsultationAuditTimeline([]);
    setPreConsultationTemplate(null);
    setVitalsDetails(null);
    setIncrementSignatureCompleted(false);
    setPaymentAudit(null);
    setPreConsult(null);
    setPreConsultationTranscription(null);
    setIsSummaryStreaming(false);
    setisManualSubmited(false);
    setNHS111ConsultationSummary(consultationData);
    setHasPreConsultTab(false);
    setIsConsultationEnabled(true);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes === null ? false : true);
    setAccessRecord(null);

    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );

    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);
    setIsTemplateSelected(true);
    setisHandleStartConsultation(false);
    setPatientNotes(
      consultationData?.notes === null ? "" : consultationData?.notes,
    );

    if (consultationData?.preConsultationTemplate) {
      await getPreConsultationByConsultationId({
        consultationId: consultationData?.consultationId,
      });
    }

    await getPaymentAuditByConsultation({
      consultationID: consultationData?.consultationId,
    });
    setPreConsultationTemplate(consultationData?.preConsultationTemplate);
    if (preConsultData?.preConsultationTemplate) {
      let preConsultTranscription = "";
      preConsultData?.preConsultationTemplate?.templateSections?.forEach(
        (section) => {
          section?.userControls?.forEach((control) => {
            if (control.label && control.value) {
              preConsultTranscription += ` ${control.label} ${control.value} `;
            }
          });
        },
      );

      setPreConsultationTranscription(preConsultTranscription?.trim());
    } else {
      setPreConsultationTranscription("");
    }

    setShowNHS111ReferralTab(false);
    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationData?.consultationId}`,
    );
    setPatientInfoTab(1);
    if (setSelectedPatientConsultation != null) {
      setPatientTabSwitcher(consultationData?.preConsultationTemplate ? 1 : 0);
    }
    if (consultationData?.consultationChat) {
      const parsedAiChatMessages = JSON.parse(
        consultationData?.consultationChat,
      );
      setAiChatSuccessMessages();
      parsedAiChatMessages.forEach((message) => {
        setAddPatientAIChatMessage(message);
      });
    }
  };

  const formatSummaryText = (text) => {
    if (!text) return text;

    const sectionTitles = [
      "Presenting Complaint",
      "Medical History",
      "Medication History",
      "Allergies",
      "Observations and Assessments",
    ];

    let formattedText = text
      .replace(/\s*(snomedCodes|SnomedCodes):\s*(\[.*?\]|null)\s*$/s, "")
      .replace(/\\n/g, "\n")
      .replace(/"/g, "");

    sectionTitles.forEach((title, index) => {
      const regex = new RegExp(`${title}:`, "g");

      formattedText = formattedText.replace(regex, `${title}:`);
    });

    formattedText = formattedText.replace(/\n{3,}/g, "\n").trim();

    return formattedText;
  };

  return {
    isPatientDetailsLoading,
    getActionDetails,
    handleArrowClick,
    formatSummaryText,
  };
};
